/**
 * sticky-atc.js — Sticky Add-to-Cart Web Component
 *
 * Pattern: floating bar at the bottom of the viewport. Appears when the
 * user scrolls past the main buy box, delegates click to the real ATC
 * button inside <product-form>, so the add-to-cart flow (validation,
 * cart drawer open, error handling) goes through ONE path.
 *
 * DOM (rendered by sections/sticky-atc.liquid):
 *   <sticky-atc role="region" aria-label="Ajouter au panier">
 *     <div class="sticky-atc" ref="bar">
 *       ...
 *       <button ref="btn" data-sticky-atc-btn>Ajouter au panier</button>
 *     </div>
 *   </sticky-atc>
 *
 * Show/hide trigger:
 *   1) IntersectionObserver on `.product-info`, `.product__wrapper`,
 *      `.product-form__actions` or `[data-buy-box-root]` (first match).
 *   2) Fallback: scroll listener (visible when window.scrollY > 600).
 *
 * Auto-hidden on /cart and /checkout/* pages.
 */

import { Component } from '@theme/component';

const HIDE_ON_PATHS = [/^\/cart(\/|$)/, /^\/checkout(\/|$)/];
const TRIGGER_SELECTORS = [
  '[data-buy-box-root]',
  '.product-form__actions',
  '.product-info',
  '.product__wrapper',
  'main-product',
  '.main-product',
];
const MAIN_ATC_SELECTOR = '.product-form__atc, product-form [type="submit"]';
const FALLBACK_SCROLL_PX = 600;

class StickyAtc extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {(() => void) | null} */
  #scrollHandler = null;
  /** @type {boolean} */
  #visible = false;

  onConnect() {
    // Bail out on cart / checkout pages
    if (HIDE_ON_PATHS.some((re) => re.test(window.location.pathname))) {
      this.remove();
      return;
    }

    const bar = this.querySelector('[data-sticky-atc-bar]') || this.firstElementChild;
    if (!bar) return;
    this.refs.bar = bar;

    const stickyBtn = this.querySelector('[data-sticky-atc-btn]');
    if (stickyBtn) {
      this.on(stickyBtn, 'click', (e) => this.#handleClick(e));
    }

    this.#setupTrigger();
    this.#setupVariantSync();
  }

  onDisconnect() {
    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }
    if (this.#scrollHandler) {
      window.removeEventListener('scroll', this.#scrollHandler);
      this.#scrollHandler = null;
    }
  }

  #setupTrigger() {
    let target = null;
    for (const sel of TRIGGER_SELECTORS) {
      target = document.querySelector(sel);
      if (target) break;
    }

    if (target && 'IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry) return;
          // Show when the buy box has scrolled ABOVE the viewport
          const rect = entry.target.getBoundingClientRect();
          const scrolledPast = rect.bottom < 0;
          if (scrolledPast) {
            this.#show();
          } else {
            this.#hide();
          }
        },
        { threshold: 0, rootMargin: '0px 0px 0px 0px' }
      );
      this.#observer.observe(target);
      return;
    }

    // Fallback scrollY watcher
    this.#scrollHandler = () => {
      if (window.scrollY > FALLBACK_SCROLL_PX) this.#show();
      else this.#hide();
    };
    window.addEventListener('scroll', this.#scrollHandler, { passive: true });
    this.#scrollHandler();
  }

  /**
   * Keeps the sticky variant select + button in sync when the main buy box
   * swaps variants. We read the hidden `input[name="id"]` on the main form.
   */
  #setupVariantSync() {
    const variantSelect = this.querySelector('[data-sticky-atc-variant]');
    const stickyBtn = this.querySelector('[data-sticky-atc-btn]');

    if (variantSelect && stickyBtn) {
      this.on(variantSelect, 'change', (e) => {
        const target = /** @type {HTMLSelectElement} */ (e.target);
        const opt = target.options[target.selectedIndex];
        stickyBtn.dataset.variantId = target.value;
        stickyBtn.disabled = !!opt.disabled;
        // Sync back to main form so Alpine/ProductForm see the change
        const mainIdInput = document.querySelector('product-form input[name="id"], .product-form input[name="id"]');
        if (mainIdInput instanceof HTMLInputElement) {
          mainIdInput.value = target.value;
          mainIdInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    // Watch for main-form variant updates → reflect in sticky select
    this.on(document, 'change', (e) => {
      const t = /** @type {HTMLElement} */ (e.target);
      if (!t || !(t instanceof HTMLElement)) return;
      if (!t.matches('.product-form__swatch input, product-form input[name="id"]')) return;
      const mainIdInput = document.querySelector('product-form input[name="id"], .product-form input[name="id"]');
      if (!(mainIdInput instanceof HTMLInputElement) || !variantSelect) return;
      if (variantSelect instanceof HTMLSelectElement) {
        variantSelect.value = mainIdInput.value;
        if (stickyBtn) stickyBtn.dataset.variantId = mainIdInput.value;
      }
    });
  }

  #handleClick(e) {
    // Delegate to the real ATC button so the main <product-form> flow runs
    // (validation, cart drawer open, error display).
    const mainBtn = document.querySelector(MAIN_ATC_SELECTOR);
    if (mainBtn instanceof HTMLButtonElement) {
      e.preventDefault();
      mainBtn.click();
    }
    // Else: fall through and let the form submit natively if present.
  }

  #show() {
    if (this.#visible) return;
    this.#visible = true;
    this.classList.add('is-visible');
    this.setAttribute('aria-hidden', 'false');
  }

  #hide() {
    if (!this.#visible) return;
    this.#visible = false;
    this.classList.remove('is-visible');
    this.setAttribute('aria-hidden', 'true');
  }
}

if (!customElements.get('sticky-atc')) {
  customElements.define('sticky-atc', StickyAtc);
}
